import React, { useEffect, useState, useContext } from "react";
import { motion } from "framer-motion";
import { Trophy, Calendar, Timer } from "lucide-react";
import { Button } from "../../components/ui/Button";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import { useFetchCases } from "src/hooks/useFetchCases";
import { InterviewContext } from "src/contexts/InterviewContext";
import { useAuth } from "src/contexts/AuthContext";
import { useLoading } from "src/contexts/LoadingContext";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

interface CaseCompetition {
  title: string;
  description: string;
  deadline: string;
  status: "upcoming" | "active" | "completed";
}

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
  },
};

const BabsonBcaContestPage = () => {
  const navigate = usePartnerAwareNavigate();
  const [clubCases, setClubCases] = useState<CaseCompetition[]>([]);
  const { firebaseUser } = useAuth();
  const { handleTimeBasedLoading } = useLoading();
  const { startInterviewHandler, settIsEndHandler } =
    useContext(InterviewContext)!;
  const [hasAttempted, setHasAttempted] = useState(false);
  const db = getFirestore();

  // Fetch partner specific cases (reuse same case ids for now)
  const stableIds = React.useMemo<number[]>(() => [1110], []);
  const { data: fetchedCases, isLoading, isError } = useFetchCases(stableIds);

  // Check if user has already attempted the competition
  useEffect(() => {
    const checkPreviousAttempt = async () => {
      if (!firebaseUser) return;

      const attemptRef = doc(db, "bca_competition_2025", firebaseUser.uid);
      const attemptDoc = await getDoc(attemptRef);

      setHasAttempted(attemptDoc.exists());
    };

    checkPreviousAttempt();
  }, [firebaseUser, db]);

  useEffect(() => {
    if (fetchedCases && fetchedCases.length > 0) {
      const transformedCases: CaseCompetition[] = fetchedCases.map(() => ({
        title: "",
        description: "",
        deadline: "November 15, 2025 at 11:59 PM EST",
        status: determineContestStatus(),
      }));
      setClubCases(transformedCases);
    }
  }, [fetchedCases]);

  const determineContestStatus = (): "upcoming" | "active" | "completed" => {
    const now = new Date();
    const startDate = new Date("2025-11-07T00:00:00-05:00");
    const endDate = new Date("2025-11-15T23:59:59-05:00");

    if (now < startDate) return "upcoming";
    if (now > endDate) return "completed";
    return "active";
  };

  const handleStartCase = async (caseId: number) => {
    if (!firebaseUser) {
      navigate("/login");
      return;
    }

    try {
      const status = determineContestStatus();
      if (status !== "active") {
        console.error("Competition is not active");
        return;
      }

      const attemptRef = doc(db, "bca_competition_2025", firebaseUser.uid);
      const attemptDoc = await getDoc(attemptRef);

      if (attemptDoc.exists()) {
        console.error("You have already participated in this competition");
        return;
      }

      await setDoc(attemptRef, {
        userId: firebaseUser.uid,
        email: firebaseUser.email,
        startTime: new Date().toISOString(),
        caseId: caseId,
      });

      const didStart = await handleTimeBasedLoading(
        startInterviewHandler(firebaseUser.uid, caseId, false, true, ""),
        {
          totalDuration: 3500,
          messages: [
            "Initializing your mock interview...",
            "Setting up your case materials...",
            "Preparing interview environment...",
            "Almost ready to begin...",
          ],
          onComplete: () => {
            // no-op
          },
        }
      );

      if (didStart && typeof didStart === "string") {
        settIsEndHandler(false);
        setHasAttempted(true);
        navigate(`/mock-case?interviewSessionId=${didStart}`, {
          state: { interviewCase: fetchedCases[0] },
        });
      } else {
        console.error("Interview could not be started. Please try again.");
      }
    } catch (err) {
      console.error("Error starting interview:", err);
    }
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8">Loading cases...</div>;
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8">
        Error loading cases. Please try again later.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="text-center mb-12"
      >
        <motion.div
          variants={itemVariants}
          className="inline-block p-3 bg-blue-100 rounded-full mb-6"
        >
          <Trophy className="w-8 h-8 text-blue-600" />
        </motion.div>
        <motion.h1
          variants={itemVariants}
          className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text"
        >
          BCA x WeKruit Mock Case Competition
        </motion.h1>
        <motion.p
          variants={itemVariants}
          className="text-gray-600 text-lg max-w-2xl mx-auto"
        >
          All Babson students are welcome to participate in this competition.
        </motion.p>
      </motion.div>

      {clubCases.map((caseComp, index) => (
        <motion.div
          key={index}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 shadow-lg border border-blue-100 mb-6 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -translate-y-16 translate-x-16 opacity-20" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-100 rounded-full translate-y-12 -translate-x-12 opacity-20" />

          <div className="flex flex-col items-center text-center space-y-6 relative">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-2 ${
                caseComp.status === "active"
                  ? "bg-green-100 text-green-600"
                  : caseComp.status === "completed"
                  ? "bg-gray-100 text-gray-600"
                  : "bg-blue-100 text-blue-600"
              }`}
            >
              <Timer className="w-4 h-4" />
              {caseComp.status === "active"
                ? "Active Now"
                : caseComp.status === "completed"
                ? "Completed"
                : "Coming Soon"}
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-2"
            >
              <h3 className="text-2xl font-bold text-gray-800">
                Get Ready for the Challenge!
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Mark your calendar for an exciting case competition experience.
              </p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl"
            >
              <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                <div className="flex items-center justify-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <p className="text-sm text-gray-500">Starts</p>
                    <p className="font-semibold text-gray-800">
                      November 7, 2025 12:00 AM EST
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                <div className="flex items-center justify-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <p className="text-sm text-gray-500">Ends</p>
                    <p className="font-semibold text-gray-800">
                      {caseComp.deadline}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-sm text-gray-600 max-w-2xl bg-white p-4 rounded-lg border border-blue-100"
            >
              <p>
                For questions, contact{" "}
                <span className="text-blue-600">external@wekruit.com</span>.
              </p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                disabled={caseComp.status !== "active" || hasAttempted}
                onClick={() =>
                  fetchedCases && handleStartCase(fetchedCases[0].id)
                }
                className={`px-8 py-3 rounded-full font-medium transform transition-all duration-200 ${
                  caseComp.status === "active" && !hasAttempted
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white opacity-75 cursor-not-allowed"
                }`}
              >
                {caseComp.status === "active"
                  ? hasAttempted
                    ? "Already Participated"
                    : "Start Case"
                  : caseComp.status === "completed"
                  ? "Competition Ended"
                  : "Coming Soon"}
              </Button>
            </motion.div>
          </div>
        </motion.div>
      ))}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8"
      >
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-50 rounded-full -translate-y-16 translate-x-16 opacity-30" />
          <h3 className="text-2xl font-bold mb-6 text-gray-800">
            Competition Prizes
          </h3>
          <ul className="space-y-6 relative">
            <li className="flex items-center gap-4 bg-gradient-to-r from-yellow-50 to-transparent p-4 rounded-lg">
              <span className="text-yellow-500 text-3xl">🏆</span>
              <div>
                <span className="font-bold text-lg">1st Place</span>
                <span className="text-blue-600 font-bold text-xl ml-3">
                  $125
                </span>
              </div>
            </li>
            <li className="flex items-center gap-4 bg-gradient-to-r from-gray-50 to-transparent p-4 rounded-lg">
              <span className="text-gray-400 text-3xl">🥈</span>
              <div>
                <span className="font-bold text-lg">2nd Place</span>
                <span className="text-blue-600 font-bold text-xl ml-3">
                  $75
                </span>
              </div>
            </li>
            <li className="flex items-center gap-4 bg-gradient-to-r from-amber-50 to-transparent p-4 rounded-lg">
              <span className="text-amber-700 text-3xl">🥉</span>
              <div>
                <span className="font-bold text-lg">3rd Place</span>
                <span className="text-blue-600 font-bold text-xl ml-3">
                  $25
                </span>
              </div>
            </li>
            <li className="flex items-center gap-4 bg-gradient-to-r from-blue-50 to-transparent p-4 rounded-lg">
              <span className="text-blue-500 text-2xl">⭐</span>
              <span className="font-medium">
                Top 10 scorers receive a free month of WeKruit Premium!
              </span>
            </li>
          </ul>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl p-8 shadow-lg border border-gray-100"
        >
          <h3 className="text-2xl font-bold mb-6 text-gray-800">
            Competition Details
          </h3>
          <ul className="space-y-5">
            <li className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-transparent rounded-lg">
              <span className="text-blue-600 text-xl">📝</span>
              <span className="font-medium">
                Complete a real-world consulting case
              </span>
            </li>
            <li className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-transparent rounded-lg">
              <span className="text-blue-600 text-xl">📊</span>
              <span className="font-medium">
                Receive comprehensive feedback on your performance
              </span>
            </li>
            <li className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-transparent rounded-lg">
              <span className="text-blue-600 text-xl">🎯</span>
              <span className="font-medium">
                Practice with industry-standard frameworks
              </span>
            </li>
            <li className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-transparent rounded-lg">
              <span className="text-blue-600 text-xl">🌟</span>
              <span className="font-medium">
                Compete with peers and showcase your skills
              </span>
            </li>
          </ul>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default BabsonBcaContestPage;
